import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useTranslation } from '../i18n';

export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading, success, already, error
  const [email, setEmail] = useState('');

  useEffect(() => {
    const verify = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/auth/verify-email/${token}`);
        const data = await res.json();
        if (res.ok) {
          setStatus(data.already_verified ? 'already' : 'success');
          setEmail(data.email || '');
        } else {
          setStatus('error');
        }
      } catch {
        setStatus('error');
      }
    };
    if (token) verify();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-5">
      <div className="w-full max-w-[400px] bg-card rounded-xl p-8 shadow-sm border text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="h-12 w-12 mx-auto text-primary animate-spin mb-4" />
            <h1 className="text-xl font-bold text-foreground">{t("verify.verifying")}</h1>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
            <h1 className="text-xl font-bold text-foreground mb-2">{t("verify.success")}</h1>
            <p className="text-sm text-muted-foreground mb-6">
              {email ? `${email} has been verified.` : 'Your email has been verified.'} You're all set.
            </p>
            <Link to="/login">
              <Button className="w-full">{t("verify.signIn")}</Button>
            </Link>
          </>
        )}

        {status === 'already' && (
          <>
            <CheckCircle className="h-12 w-12 mx-auto text-blue-500 mb-4" />
            <h1 className="text-xl font-bold text-foreground mb-2">{t("verify.alreadyVerified")}</h1>
            <p className="text-sm text-muted-foreground mb-6">
              Your email is already verified. You can sign in.
            </p>
            <Link to="/login">
              <Button className="w-full">Sign In</Button>
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
            <h1 className="text-xl font-bold text-foreground mb-2">{t("verify.invalidLink")}</h1>
            <p className="text-sm text-muted-foreground mb-6">
              This verification link is invalid or has expired.
            </p>
            <Link to="/login">
              <Button variant="outline" className="w-full">{t("verify.backToLogin")}</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
